import { IProjectPosition } from '../../../interfaces/projectPosition';
import { ITableAction, ITableColumn } from '../../../interfaces/tableProps';
import { IProjectAvailabilityRequirement } from '../../../interfaces/projectAvailabilityRequirement';
import { IPaymentRate } from '../../../interfaces/paymentRate';
import AppTable from '../AppTable';
import { IProjectPositionSkill } from '../../../interfaces/projectPositionSkill';
import { IProjectLanguageRequirement } from '../../../interfaces/projectLanuageRequirement';
import { useCallback, useMemo } from 'react';
import { Delete, Edit, Groups, PersonAdd } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { TEXTS } from '../../../core/texts';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../interfaces/errorResponse';
import { parseAxiosError } from '../../../services/axios';
import { ADD_APPLICANT_QUERY_PARAM, APPLICANTS_QUERY_PARAM, POSITION_QUERY_PARAM } from '../../project-opportunities/project-opportunity-entity/ProjectOpportunityEntityPositions';
import { PROJECT_POSITION_STATUS_MAP, ProjectPositionStatus } from '../../../enums/projectPositionStatus';
import { IProjectOpportunity } from '../../../interfaces/projectOpportunity';
import { useConfirmationDialog } from '../../../+state/useConfirmationDialog';
import { useNotification } from '../../../+state/useNotification';
import {
  MODAL_PAGINATION_PAGE_NAMESPACE,
  MODAL_PAGINATION_SIZE_NAMESPACE,
  PAGINATION_DEFAULT_PAGE, PAGINATION_DEFAULT_SIZE
} from '../../../core/pagination';
import { PAYMENT_RATE_MAP } from '../../../enums/paymentRateType';
import { PROFICIENCY_MAP } from '../../../interfaces/language';

interface IProps {
  positions: IProjectPosition[],
  handleRemovePosition: (positionId: string) => Promise<IProjectPosition>;
  refreshPositions: () => void,
  project: IProjectOpportunity,
};

const columns: ITableColumn<keyof IProjectPosition>[] = [
  { name: 'positionName', label: 'Position Name' },
  { name: 'roleName', label: 'Role Name' },
  { name: 'title', label: 'Title' },
  {
    name: 'skills',
    label: 'Skills',
    format: (val: IProjectPositionSkill[]) => val.map(s => s.skill).join(', '),
    truncate: 30
  },
  { name: 'status', label: 'Status', format: (val: ProjectPositionStatus) => PROJECT_POSITION_STATUS_MAP[val] },
  {
    name: 'availability',
    label: 'Availability',
    format: (val: IProjectAvailabilityRequirement) => `${val.fromHour} - ${val.toHour} (${val.timezone})`
  },
  {
    name: 'preferredRate',
    label: 'Rate',
    format: (val: IPaymentRate) => `${val.amount.toFixed()} ${val.currency} - ${PAYMENT_RATE_MAP[val.type]}`
  },
  {
    name: 'language',
    label: 'Language',
    format: (val: IProjectLanguageRequirement) => `${val.languageCode} - ${PROFICIENCY_MAP[val.minimumProficiencyLevel]}`
  },
];

const PositionsTable = ({ positions, handleRemovePosition, refreshPositions, project }: IProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openDialog } = useConfirmationDialog();
  const { openErrorToast, openSuccessToast } = useNotification();

  const handleEditIcon = useCallback((position: IProjectPosition) => {
    searchParams.set(POSITION_QUERY_PARAM, position.id);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleApplicantsIcon = useCallback((position: IProjectPosition) => {
    searchParams.set(APPLICANTS_QUERY_PARAM, position.id);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleAddApplicantIcon = useCallback((position: IProjectPosition) => {
    searchParams.set(ADD_APPLICANT_QUERY_PARAM, position.id);
    searchParams.set(MODAL_PAGINATION_PAGE_NAMESPACE, PAGINATION_DEFAULT_PAGE.toString());
    searchParams.set(MODAL_PAGINATION_SIZE_NAMESPACE, PAGINATION_DEFAULT_SIZE.toString());
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const mutation = useMutation({
    mutationFn: (positionId: string) => handleRemovePosition(positionId),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_DELETED);
      refreshPositions();
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const handleDeleteIcon = useCallback((position: IProjectPosition) => {
    openDialog({ cb: () => mutation.mutate(position.id) });
  }, [openDialog, mutation]);

  const actions = useMemo<ITableAction[]>(() => ([
    {
      icon: <PersonAdd color={'secondary'}/>,
      tooltip: 'Add Applicant',
      onClick: handleAddApplicantIcon,
      condition: (p: IProjectPosition) => p.status === ProjectPositionStatus.OPEN_FOR_APPLICANTS
    },
    {
      icon: <Groups color={'secondary'}/>,
      tooltip: 'Applicants',
      onClick: handleApplicantsIcon
    },
    {
      icon: <Edit color={'primary'}/>,
      tooltip: 'Edit Position',
      onClick: handleEditIcon
    },
    {
      icon: <Delete color={'error'}/>,
      tooltip: 'Delete',
      onClick: handleDeleteIcon
    }
  ]), [handleEditIcon, handleDeleteIcon, handleApplicantsIcon, handleAddApplicantIcon]);

  return (
    <AppTable data={{ list: positions }} columns={columns} actions={actions}/>
  );
};

export default PositionsTable;